import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { services, aboutMedia } from "../mock";

const Services = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[#FDFDFD]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20"
        >
          <div className="lg:col-span-5">
            <p
              data-testid="services-overline"
              className="text-xs md:text-sm uppercase tracking-[0.3em] font-semibold text-[#172554] mb-6"
            >
              — Practice
            </p>
            <h2
              data-testid="services-title"
              className="font-['Playfair_Display'] text-[#0A1128] text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-medium"
            >
              One focus.
              <br />
              One <span className="italic">trusted</span> partner.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-[#475569] text-lg md:text-xl leading-relaxed font-['Outfit'] font-light">
              We work end-to-end on the most consequential transaction in a
              business's life — preparing, positioning, and closing the deal —
              with senior advisors leading every step.
            </p>
          </div>
        </motion.div>

        {/* Services grid */}
        <div className="grid grid-cols-1 gap-8 max-w-3xl mx-auto">
          {services.map((svc, i) => {
            const Icon = svc.icon;
            return (
              <motion.div
                key={svc.id}
                data-testid={`service-card-${svc.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: i * 0.15 }}
                className="group bg-white border border-slate-200 p-10 md:p-12 hover:-translate-y-1 transition-all duration-500 hover:shadow-2xl hover:border-[#CFA660]/40 relative overflow-hidden"
              >
                {/* Number */}
                <div className="absolute top-8 right-8 font-['Playfair_Display'] text-[#E2E8F0] text-7xl leading-none italic select-none group-hover:text-[#CFA660]/30 transition-colors duration-500">
                  0{i + 1}
                </div>

                <div className="relative">
                  <div className="w-14 h-14 border border-[#0A1128] flex items-center justify-center mb-8 group-hover:bg-[#0A1128] transition-colors duration-300">
                    <Icon className="w-6 h-6 text-[#0A1128] group-hover:text-[#CFA660] transition-colors duration-300" />
                  </div>

                  <h3
                    className="font-['Playfair_Display'] text-[#0A1128] text-2xl md:text-3xl font-medium tracking-tight mb-4"
                  >
                    {svc.title}
                  </h3>
                  <p className="text-[#475569] text-base md:text-lg leading-relaxed font-['Outfit'] mb-8">
                    {svc.summary}
                  </p>

                  <ul className="space-y-3 mb-10">
                    {svc.points.map((p) => (
                      <li
                        key={p}
                        className="flex items-start gap-3 text-[#0A1128] text-sm font-['Outfit']"
                      >
                        <Check className="w-4 h-4 mt-0.5 text-[#CFA660] flex-shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`service-cta-${svc.id}`}
                    onClick={() => scrollTo("contact")}
                    className="inline-flex items-center gap-2 text-[#0A1128] text-sm font-semibold uppercase tracking-wider border-b border-[#0A1128] pb-1 hover:gap-3 hover:text-[#172554] transition-all duration-300"
                  >
                    Discuss an engagement
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Image strip */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="mt-20 relative h-72 md:h-96 overflow-hidden"
        >
          <img
            src={aboutMedia.boardroom}
            alt="Boardroom discussion"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0A1128]/60" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-3xl px-8 md:px-16">
              <p className="text-[#CFA660] text-xs uppercase tracking-[0.3em] mb-4">
                A note on our approach
              </p>
              <p className="text-white text-2xl md:text-3xl font-['Playfair_Display'] leading-snug italic">
                "Our clients rely on our total anonymity and confidence which
                are cornerstones to our success — and the reasons we receive so
                many referrals from our clients."
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
